import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect } from 'react';
import {
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { ThemedText } from '../../components/ThemedText';
import { ThemedView } from '../../components/ThemedView';
import { BodyText, PageTitle, SubtitleText } from '@/components/ui/Typography';
import { logger } from '../../lib/logger';

export default function EmailConfirmationScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const { email } = params;

  useEffect(() => {
    // Auto-redirect to login after 10 seconds
    const timer = setTimeout(() => {
      router.replace('/auth/login' as any);
    }, 10000);

    return () => clearTimeout(timer);
  }, [router]);

  const navigateToLogin = () => {
    router.replace('/auth/login' as any);
  };

  const resendEmail = () => {
    // This could trigger a resend function if implemented
    __DEV__ && logger.debug('Resend email functionality would go here');
  };

  return (
    <View style={styles.container}>
      <ScrollView 
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={Platform.OS === 'web'}
      >
        <ThemedView style={styles.card}>
          <View style={styles.iconContainer}>
            <View style={styles.emailIcon}>
              <Text style={styles.emailIconText}>✉️</Text>
            </View>
          </View>
          
          <PageTitle ta="center" fontWeight="700" color="#1a1a1a" mb={16}>
            Check Your Email
          </PageTitle>
          
          <BodyText ta="center" color="#666" mb={8}>
            We’ve sent a confirmation link to
          </BodyText>
          
          <SubtitleText ta="center" color="#007bff" mb={16}>
            {email || 'your email address'}
          </SubtitleText>
          
          <BodyText ta="center" color="#666" mb={32}>
            Click the link in the email to verify your account and complete your registration.
          </BodyText>

          <View style={styles.instructionsContainer}>
            <ThemedText style={styles.instructionsTitle}>
              What to do next:
            </ThemedText>
            <ThemedText style={styles.instruction}>
              1. Check your email inbox (and spam folder)
            </ThemedText>
            <ThemedText style={styles.instruction}>
              2. Click the verification link in the email
            </ThemedText>
            <ThemedText style={styles.instruction}>
              3. You’ll be automatically signed in
            </ThemedText>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.secondaryButton]}
              onPress={resendEmail}
            >
              <Text style={styles.secondaryButtonText}>
                Resend Email
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.primaryButton]}
              onPress={navigateToLogin}
            >
              <Text style={styles.buttonText}>
                Back to Sign In
              </Text>
            </TouchableOpacity>
          </View>

          <View style={styles.footer}>
            <ThemedText style={styles.footerText}>
              This page will automatically redirect to sign in in 10 seconds
            </ThemedText>
          </View>
        </ThemedView>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? '20%' : 20,
    paddingVertical: Platform.OS === 'web' ? 40 : 60,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 32,
    maxWidth: Platform.OS === 'web' ? 500 : undefined,
    alignSelf: 'center',
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  emailIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e3f2fd',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emailIconText: {
    fontSize: 40,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 16,
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 8,
    color: '#666',
  },
  email: {
    fontSize: 18,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 16,
    color: '#007bff',
  },
  description: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    color: '#666',
    lineHeight: 24,
  },
  instructionsContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 20,
    marginBottom: 32,
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
    color: '#1a1a1a',
  },
  instruction: {
    fontSize: 14,
    marginBottom: 8,
    color: '#666',
    paddingLeft: 8,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
  },
  primaryButton: {
    backgroundColor: '#007bff',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  secondaryButton: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  secondaryButtonText: {
    color: '#666',
    fontSize: 16,
    fontWeight: '500',
  },
  footer: {
    alignItems: 'center',
  },
  footerText: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    fontStyle: 'italic',
  },
});